import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Input, Card, Row, Col, Typography, Space, Modal, TimePicker, message } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Calendar, SlotInfo, Event } from "react-big-calendar";
import dayjs, { Dayjs } from "dayjs";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { TeamMember, TimeBlock, migrateLegacySchedule } from "../../../types/admin/TeamTypes";
import { fromPickerValue, wallClockParts } from "../../../utils/schedule/wallClock";
import { calendarLocalizer } from "../../../pages/healthcare/appointments/components/calendarLocalizer";
import UserProfileScheduleOverrides from "../../../pages/profile/sections/UserProfileScheduleOverrides";

const { Text } = Typography;

interface AvailabilityTabProps {
	teamMember: TeamMember;
	setTeamMember: React.Dispatch<React.SetStateAction<any>>;
}

interface CalendarEvent extends Event {
	id: string;
	dayOfWeek: string;
	blockId: string;
}

const getDayName = (date: Date): string => {
	const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
	return days[date.getDay()];
};

const createReferenceDate = (dayName: string, hours: number, minutes: number): Date => {
	// Offsets from a MONDAY: the shared localizer starts the week there, so a Sunday-based
	// reference would push six of the seven days past the end of the rendered week.
	const dayMap: Record<string, number> = {
		Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3,
		Friday: 4, Saturday: 5, Sunday: 6,
	};
	const referenceWeekStart = new Date(2024, 0, 1);
	const result = new Date(referenceWeekStart);
	result.setDate(result.getDate() + dayMap[dayName]);
	result.setHours(hours, minutes, 0, 0);
	return result;
};

const convertBlocksToEvents = (schedule: Record<string, TimeBlock[]>): CalendarEvent[] => {
	const events: CalendarEvent[] = [];
	Object.entries(schedule).forEach(([dayName, blocks]) => {
		if (!Array.isArray(blocks)) return;
		blocks.forEach((block) => {
			const startParts = wallClockParts(block.start);
			const endParts = wallClockParts(block.end);
			if (startParts && endParts) {
				events.push({
					id: `${dayName}-${block.id}`,
					title: "Available",
					start: createReferenceDate(dayName, startParts.hours, startParts.minutes),
					end: createReferenceDate(dayName, endParts.hours, endParts.minutes),
					dayOfWeek: dayName,
					blockId: block.id,
				});
			}
		});
	});
	return events;
};

const AvailabilityTab: React.FC<AvailabilityTabProps> = ({ teamMember, setTeamMember }) => {
	const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date; dayOfWeek: string } | null>(null);
	const [showAddModal, setShowAddModal] = useState(false);
	const [newBlockStart, setNewBlockStart] = useState<Dayjs | null>(null);
	const [newBlockEnd, setNewBlockEnd] = useState<Dayjs | null>(null);

	useEffect(() => {
		if (teamMember.team_member_info?.availability_schedule) {
			const schedule = teamMember.team_member_info.availability_schedule;
			const needsMigration = Object.values(schedule).some(
				(dayData: any) => dayData && !Array.isArray(dayData) && typeof dayData === "object"
			);
			if (needsMigration) {
				const migratedSchedule = migrateLegacySchedule(schedule);
				setTeamMember((prev: TeamMember) => ({
					...prev,
					team_member_info: { ...prev.team_member_info, availability_schedule: migratedSchedule },
				}));
			}
		}
	}, []);

	const schedule = teamMember.team_member_info?.availability_schedule || {};
	const events = useMemo(() => convertBlocksToEvents(schedule as Record<string, TimeBlock[]>), [schedule]);

	const { defaultDate, min, max } = useMemo(() => {
		const refDate = new Date(2024, 0, 1);
		return {
			defaultDate: refDate,
			min: new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), 0, 0, 0),
			max: new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), 23, 59, 59),
		};
	}, []);

	const handleSelectSlot = useCallback((slotInfo: SlotInfo) => {
		const dayOfWeek = getDayName(slotInfo.start);
		setSelectedSlot({ start: slotInfo.start, end: slotInfo.end, dayOfWeek });
		setNewBlockStart(dayjs(slotInfo.start));
		setNewBlockEnd(dayjs(slotInfo.end));
		setShowAddModal(true);
	}, []);

	const handleAddBlock = () => {
		if (!selectedSlot || !newBlockStart || !newBlockEnd) return;
		const newBlock: TimeBlock = {
			id: `block-${Date.now()}`,
			start: fromPickerValue(newBlockStart),
			end: fromPickerValue(newBlockEnd),
		};
		setTeamMember((prev: TeamMember) => {
			const currentBlocks = (schedule as Record<string, TimeBlock[]>)[selectedSlot.dayOfWeek] || [];
			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					availability_schedule: {
						...schedule,
						[selectedSlot.dayOfWeek]: [...currentBlocks, newBlock],
					},
				},
			};
		});
		message.success("Availability block added");
		setShowAddModal(false);
		setSelectedSlot(null);
		setNewBlockStart(null);
		setNewBlockEnd(null);
	};

	const handleSelectEvent = useCallback(
		(event: CalendarEvent) => {
			Modal.confirm({
				title: "Delete Availability Block",
				content: `Remove availability on ${event.dayOfWeek} from ${dayjs(event.start).format("h:mm A")} to ${dayjs(event.end).format("h:mm A")}?`,
				okText: "Delete",
				okType: "danger",
				onOk: () => {
					setTeamMember((prev: TeamMember) => {
						const currentBlocks = (schedule as Record<string, TimeBlock[]>)[event.dayOfWeek] || [];
						return {
							...prev,
							team_member_info: {
								...prev.team_member_info,
								availability_schedule: {
									...schedule,
									[event.dayOfWeek]: currentBlocks.filter((b) => b.id !== event.blockId),
								},
							},
						};
					});
					message.success("Availability block removed");
				},
			});
		},
		[schedule, setTeamMember]
	);

	const eventStyleGetter = () => ({
		style: {
			backgroundColor: "var(--mg-primary)",
			borderRadius: 4,
			opacity: 0.85,
			color: "#fff",
			border: "none",
			display: "block",
		},
	});

	const handleConsultationFeeChange = (value: string) => {
		const numericValue = value ? parseFloat(value) : 0;
		setTeamMember((prev: TeamMember) => ({
			...prev,
			team_member_info: {
				...prev.team_member_info,
				consultation_fee: isNaN(numericValue) ? 0 : numericValue,
			},
		}));
	};

	const handlePracticingLocationsChange = (value: string) => {
		const locations = value.split(",").map((s) => s.trim()).filter(Boolean);
		setTeamMember((prev: TeamMember) => ({
			...prev,
			team_member_info: { ...prev.team_member_info, practicing_locations: locations },
		}));
	};

	const handleBillingChange = (field: string, value: string) => {
		setTeamMember((prev: TeamMember) => ({
			...prev,
			team_member_info: {
				...prev.team_member_info,
				billing_information: { ...(prev.team_member_info?.billing_information || {}), [field]: value },
			},
		}));
	};

	const handleMalpracticeChange = (field: string, value: string) => {
		setTeamMember((prev: TeamMember) => ({
			...prev,
			team_member_info: {
				...prev.team_member_info,
				malpractice_insurance_details: { ...(prev.team_member_info?.malpractice_insurance_details || {}), [field]: value },
			},
		}));
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{/* Basics */}
			<Row gutter={[16, 16]}>
				<Col xs={24} md={12}>
					<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Practicing Locations</label>
					<Input
						value={teamMember.team_member_info?.practicing_locations?.join(", ") || ""}
						onChange={(e) => handlePracticingLocationsChange(e.target.value)}
						placeholder="e.g., Melbourne, Sydney"
					/>
				</Col>
				<Col xs={24} md={12}>
					<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Consultation Fee (AUD)</label>
					<Input
						value={String(teamMember.team_member_info?.consultation_fee || "")}
						onChange={(e) => handleConsultationFeeChange(e.target.value)}
						placeholder="0.00"
						prefix="$"
					/>
				</Col>
			</Row>

			{/* Calendar */}
			<Card
				title="Weekly Availability Schedule"
				size="small"
				extra={
					<Space>
						<InfoCircleOutlined />
						<Text type="secondary" style={{ fontSize: 12 }}>
							Click and drag to add availability; click blocks to remove
						</Text>
					</Space>
				}
			>
				<div style={{ height: 600 }}>
					<Calendar
						localizer={calendarLocalizer}
						events={events}
						defaultView="week"
						views={["week"]}
						defaultDate={defaultDate}
						min={min}
						max={max}
						selectable
						onSelectSlot={handleSelectSlot}
						onSelectEvent={(e) => handleSelectEvent(e as CalendarEvent)}
						eventPropGetter={eventStyleGetter}
						step={15}
						timeslots={4}
						toolbar={false}
						formats={{ dayFormat: "ddd", timeGutterFormat: "h A" }}
					/>
				</div>
			</Card>

			<UserProfileScheduleOverrides teamMember={teamMember} isEditing onOverridesChange={() => {}} />

			{/* Billing */}
			<Card title="Billing Information" size="small">
				<Row gutter={[16, 16]}>
					<Col xs={24} md={12}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Account Number</label>
						<Input
							value={teamMember.team_member_info?.billing_information?.account_number || ""}
							onChange={(e) => handleBillingChange("account_number", e.target.value)}
							placeholder="Enter account number"
						/>
					</Col>
					<Col xs={24} md={12}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Billing Address</label>
						<Input
							value={teamMember.team_member_info?.billing_information?.billing_address || ""}
							onChange={(e) => handleBillingChange("billing_address", e.target.value)}
							placeholder="Enter billing address"
						/>
					</Col>
				</Row>
			</Card>

			{/* Insurance */}
			<Card title="Insurance Details" size="small">
				<Row gutter={[16, 16]}>
					<Col xs={24} md={8}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Provider</label>
						<Input
							value={teamMember.team_member_info?.malpractice_insurance_details?.provider || ""}
							onChange={(e) => handleMalpracticeChange("provider", e.target.value)}
							placeholder="Insurance provider"
						/>
					</Col>
					<Col xs={24} md={8}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Policy Number</label>
						<Input
							value={teamMember.team_member_info?.malpractice_insurance_details?.policy_number || ""}
							onChange={(e) => handleMalpracticeChange("policy_number", e.target.value)}
							placeholder="Policy number"
						/>
					</Col>
					<Col xs={24} md={8}>
						<label style={{ display: "block", marginBottom: 4, fontSize: 13 }}>Coverage Amount</label>
						<Input
							value={teamMember.team_member_info?.malpractice_insurance_details?.coverage_amount || ""}
							onChange={(e) => handleMalpracticeChange("coverage_amount", e.target.value)}
							placeholder="Coverage amount"
							prefix="$"
						/>
					</Col>
				</Row>
			</Card>

			{/* Add Block Modal */}
			<Modal
				title={`Add Availability — ${selectedSlot?.dayOfWeek}`}
				open={showAddModal}
				onOk={handleAddBlock}
				onCancel={() => {
					setShowAddModal(false);
					setSelectedSlot(null);
					setNewBlockStart(null);
					setNewBlockEnd(null);
				}}
				okText="Add Block"
			>
				<Space direction="vertical" style={{ width: "100%" }} size="large">
					<div>
						<Text strong>Start Time</Text>
						<TimePicker
							value={newBlockStart}
							onChange={setNewBlockStart}
							format="h:mm A"
							use12Hours
							minuteStep={15}
							style={{ width: "100%", marginTop: 8 }}
						/>
					</div>
					<div>
						<Text strong>End Time</Text>
						<TimePicker
							value={newBlockEnd}
							onChange={setNewBlockEnd}
							format="h:mm A"
							use12Hours
							minuteStep={15}
							style={{ width: "100%", marginTop: 8 }}
						/>
					</div>
				</Space>
			</Modal>
		</div>
	);
};

export default AvailabilityTab;
